import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { DateTimeDisplay } from '../components/common/DateTimeDisplay';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { getRoundDetails } from '../services/reportService';
import type { IGoalScorer } from '../types';

const getNestedValue = (obj: any, path: string, defaultValue: any = null): any => {
  if (!obj || !path) return defaultValue;
  const val = path.split('.').reduce((acc: any, key: string) => {
    if (acc && acc[key] !== undefined) return acc[key];
    return defaultValue;
  }, obj);
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return val ?? defaultValue;
};

const groupedDetails: Record<string, { id: string; label: string }[]> = {
  Transaction_Details: [
    { id: 'betTxnId', label: 'Bet Transaction ID' },
    { id: 'roundId', label: 'Round ID' },
    { id: 'betStatus', label: 'Status' },
    { id: 'resolved', label: 'Resolution Status' },
    { id: 'currency', label: 'Currency' },
    { id: 'betAmount', label: 'Bet Amount' },
    { id: 'winAmount', label: 'Win Amount' },
  ],
  Dates: [
    { id: 'betPlacedAt', label: 'Bet Placed At' },
    { id: 'betSettledAt', label: 'Bet Settled At' },
  ],
  Player_Info: [
    { id: 'playerName', label: 'Player Name' },
    { id: 'externalPlayerId', label: 'External Player ID' },
  ],
  Match_Info: [
    { id: 'matchInfo.matchStatus', label: 'Match Status' },
    { id: 'matchInfo.myTeamScore', label: 'My Team Score' },
    { id: 'matchInfo.oppTeamScore', label: 'Opponent Score' },
  ],
  Market_Info: [
    { id: 'marketType', label: 'Market Type' },
    { id: 'oddDetails.win', label: 'Win' },
    { id: 'oddDetails.goals', label: 'Goals' },
    { id: 'oddDetails.teamId', label: 'Team ID' },
    { id: 'oddDetails.playerId', label: 'Player ID' },
    { id: 'oddDetails.team1', label: 'Team 1 Score' },
    { id: 'oddDetails.team2', label: 'Team 2 Score' },
    { id: 'oddDetails.value', label: 'Value' },
    { id: 'oddDetails.type', label: 'Type' },
    { id: 'oddDetails.handicap', label: 'Handicap' },
  ],
  Odd_Info: [{ id: 'oddAfterMargin', label: 'Odd' }],
};

const DATE_FIELDS = new Set(['betPlacedAt', 'betSettledAt']);

const resolvedColors: Record<string, string> = {
  won: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  lost: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  refunded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
};

const betStatusColors: Record<string, string> = {
  debit_completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  credit_started: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  credit_completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  refund_completed: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  debit_failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  credit_failed: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  refund_failed: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  refund_started: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const StatusChip = ({ value, colorMap }: { value: string; colorMap: Record<string, string> }) => {
  const cls = colorMap[value] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{value}</span>
  );
};

const FieldCard = ({ id, label, data }: { id: string; label: string; data: any }) => {
  const rawValue = getNestedValue(data, id);
  let content: React.ReactNode;

  if (id === 'resolved') {
    content = rawValue ? (
      <StatusChip value={rawValue} colorMap={resolvedColors} />
    ) : (
      <span className="text-sm font-semibold">-</span>
    );
  } else if (id === 'betStatus') {
    content = rawValue ? (
      <StatusChip value={rawValue} colorMap={betStatusColors} />
    ) : (
      <span className="text-sm font-semibold">-</span>
    );
  } else if (DATE_FIELDS.has(id)) {
    content = <DateTimeDisplay date={rawValue} className="text-sm font-semibold" />;
  } else {
    content = <p className="text-sm font-semibold break-all">{rawValue ?? '-'}</p>;
  }

  return (
    <div className="border rounded-lg p-3 min-w-[160px] max-w-[280px] shadow-sm bg-card flex flex-col gap-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      {content}
    </div>
  );
};

export const TransactionDetails = () => {
  const { betTxnId } = useParams<{ betTxnId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const backUrl = (location.state as any)?.from ?? '/transactions';
  const [goalScorersOpen, setGoalScorersOpen] = useState(false);
  const [inputId, setInputId] = useState(betTxnId ?? '');
  const [activeId, setActiveId] = useState(betTxnId ?? '');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['round-details', activeId],
    queryFn: () => getRoundDetails(activeId),
    enabled: !!activeId,
  });

  const handleSearch = () => {
    if (inputId.trim()) setActiveId(inputId.trim());
  };

  return (
    <Layout>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Button variant="ghost" onClick={() => navigate(backUrl)}>
            &larr; Back to Transactions
          </Button>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Enter Bet Transaction ID"
              value={inputId}
              onChange={(e) => setInputId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-72 h-9"
            />
            <Button size="sm" onClick={handleSearch}>
              Get Info
            </Button>
          </div>
        </div>

        {isLoading && <p className="text-muted-foreground">Loading...</p>}
        {isError && <p className="text-destructive">Failed to load round details.</p>}

        {data && (
          <div className="space-y-4">
            {Object.entries(groupedDetails).map(([sectionTitle, columns]) => {
              const hasGoalScorers =
                sectionTitle === 'Match_Info' &&
                data.goalScorers &&
                (data.goalScorers.myTeam.length > 0 || data.goalScorers.opponent.length > 0);

              return (
                <Card key={sectionTitle} className="shadow-md">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-bold">{sectionTitle.replace(/_/g, ' ')}</CardTitle>
                      {hasGoalScorers && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setGoalScorersOpen((o) => !o)}
                          className="text-xs"
                        >
                          {goalScorersOpen ? 'Hide' : 'View'} Goal Scorers
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {columns.map((column) => (
                        <FieldCard key={column.id} id={column.id} label={column.label} data={data} />
                      ))}
                    </div>

                    {/* Goal Scorers - inline under Match Info */}
                    {sectionTitle === 'Match_Info' && goalScorersOpen && data.goalScorers && (
                      <div className="mt-4 grid grid-cols-2 gap-6 border-t pt-4">
                        <div>
                          <h4 className="font-semibold mb-2 text-sm">My Team</h4>
                          {data.goalScorers.myTeam.length === 0 ? (
                            <p className="text-muted-foreground text-sm">No goals</p>
                          ) : (
                            <ul className="text-sm space-y-1">
                              {data.goalScorers.myTeam.map((g: IGoalScorer) => (
                                <li key={g._id}>
                                  {g.name ?? g.playerId} ({g.time}')
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2 text-sm">Opponent</h4>
                          {data.goalScorers.opponent.length === 0 ? (
                            <p className="text-muted-foreground text-sm">No goals</p>
                          ) : (
                            <ul className="text-sm space-y-1">
                              {data.goalScorers.opponent.map((g: IGoalScorer) => (
                                <li key={g._id}>
                                  {g.name ?? g.playerId} ({g.time}')
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};
